import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Header from '../components/Header';

const { FiInfo, FiCheck } = FiIcons;

const featureTabs = [
  { id: 'lightweight', label: 'Lehký a odolný materiál' },
  { id: 'flexibility', label: 'Extrémní flexibilita' },
  { id: 'fireproof', label: 'Požární odolnost' },
  { id: 'waterproof', label: 'Voděodolnost a nárazy' }
];

const FeaturesExtended = () => {
  const [activeTab, setActiveTab] = useState('lightweight');

  return (
    <motion.div 
      className="min-h-screen bg-arch-light dark:bg-dark-bg text-arch-dark dark:text-gray-200 transition-colors duration-300"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Header />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <header className="mb-12 pb-6">
            <h1 className="text-4xl font-bold tracking-tight mb-4 text-arch-dark dark:text-white">Detailní vlastnosti</h1>
            <p className="text-accent-teal font-mono text-sm">Hloubkový pohled na technologický náskok našich kompozitů.</p>
          </header>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap border border-arch-border dark:border-dark-border mb-12 w-full lg:w-max bg-white dark:bg-dark-surface">
            {featureTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 lg:flex-none px-6 py-4 text-sm font-bold tracking-wider uppercase transition-colors border-b lg:border-b-0 lg:border-r last:border-0 border-arch-border dark:border-dark-border ${
                  activeTab === tab.id 
                    ? 'bg-black text-white dark:bg-white dark:text-black' 
                    : 'text-arch-dark dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="min-h-[50vh]">
            {activeTab === 'lightweight' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-16"
              >
                {/* Introduction Section */}
                <section className="bg-white dark:bg-dark-surface border border-arch-border dark:border-dark-border p-8 md:p-12 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-2 h-full bg-brand-500"></div>
                  <h2 className="text-2xl font-bold text-arch-dark dark:text-white mb-6 uppercase tracking-widest text-sm">Popis vlastnosti</h2>
                  <p className="text-arch-gray dark:text-gray-400 leading-relaxed max-w-4xl text-lg">
                    Materiály (zejména WPC a SPC panely) představují technologickou špičku v oblasti interiérového a exteriérového designu,
                    protože dokáží kombinovat vysokou pevnost s velmi nízkou hustotou. Tato vlastnost zajišťuje extrémně dlouhou životnost produktů,
                    aniž by docházelo k nadměrnému zatěžování nosných konstrukcí budov nebo stěn. Produkty jsou odolné proti vodě, plísním,
                    hmyzu a deformacím, což z nich činí ideální náhradu za klasické dřevěné nebo kamenné obklady.
                  </p>
                </section>

                {/* RTB Section */}
                <section>
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-8 h-[1px] bg-brand-500"></div>
                    <h2 className="text-xl font-bold text-arch-dark dark:text-white uppercase tracking-widest text-sm">Reason to Believe (RTB) – Proč to takto funguje?</h2>
                  </div>
                  
                  <p className="text-arch-gray dark:text-gray-400 mb-10 max-w-3xl">
                    Důvody pro výjimečný poměr váhy a odolnosti vycházejí z unikátního složení a výrobního procesu:
                  </p>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="p-8 border border-arch-border dark:border-dark-border bg-white dark:bg-dark-surface group hover:border-accent-teal transition-colors">
                      <div className="w-10 h-10 bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border flex items-center justify-center mb-6">
                        <span className="font-mono text-accent-teal font-bold">01</span>
                      </div>
                      <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-4">Inovativní materiálové složení</h3>
                      <p className="text-sm text-arch-gray dark:text-gray-400 leading-relaxed mb-4">
                        Základem je směs nanovlákenného prášku nebo bambusových vláken kombinovaných s polymery (PVC) a vápenatým práškem.
                      </p>
                      <div className="bg-arch-light dark:bg-[#1A1D24] p-4 text-xs font-mono text-arch-gray dark:text-gray-500 border-l-2 border-accent-teal">
                        Typické složení WPC panelu zahrnuje 85 % PVC masterbatche, 7 % pryskyřice, 5 % vápenného prášku a 3 % bambusového vlákna. 
                        Tato synergie přírodních vláken a polymerů vytváří pevnou vnitřní strukturu, která je lehčí než masivní dřevo.
                      </div>
                    </div>

                    <div className="p-8 border border-arch-border dark:border-dark-border bg-white dark:bg-dark-surface group hover:border-brand-500 transition-colors">
                      <div className="w-10 h-10 bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border flex items-center justify-center mb-6">
                        <span className="font-mono text-brand-500 font-bold">02</span>
                      </div>
                      <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-4">Extrémně nízká hustota při zachování pevnosti</h3>
                      <p className="text-sm text-arch-gray dark:text-gray-400 leading-relaxed mb-4">
                        WPC panely dosahují hustoty v rozmezí 483 kg/m³ až 700 kg/m³. To znamená, že materiál je snadno ovladatelný, ale strukturálně velmi stabilní.
                      </p>
                      <div className="flex items-start space-x-3 mb-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                        <span className="text-xs text-arch-gray dark:text-gray-300">Standardní panel (5 mm) váží pouze 12–13 kg</span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                        <span className="text-xs text-arch-gray dark:text-gray-300">Ohybová pevnost dosahuje úctyhodných 7,8 MPa</span>
                      </div>
                    </div>

                    <div className="p-8 border border-arch-border dark:border-dark-border bg-white dark:bg-dark-surface group hover:border-accent-sage transition-colors">
                      <div className="w-10 h-10 bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border flex items-center justify-center mb-6">
                        <span className="font-mono text-accent-sage font-bold">03</span>
                      </div>
                      <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-4">Vysoká odolnost díky vícevrstvé struktuře</h3>
                      <p className="text-sm text-arch-gray dark:text-gray-400 leading-relaxed">
                        Odolnost je posílena speciálními vrstvami, jako je SPC anti-kolizní vrstva a vysoce pevné krystalické koextrudované jádro. 
                        Díky polymerové bázi materiál neabsorbuje vodu, což zabraňuje bobtnání, hnilobě a deformacím, které jsou běžné u tradičních dřevěných roštů.
                      </p>
                    </div>

                    <div className="p-8 border border-arch-border dark:border-dark-border bg-white dark:bg-dark-surface group hover:border-accent-clay transition-colors">
                      <div className="w-10 h-10 bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border flex items-center justify-center mb-6">
                        <span className="font-mono text-accent-clay font-bold">04</span>
                      </div>
                      <h3 className="text-lg font-bold text-arch-dark dark:text-white mb-4">Technologie koextruze</h3>
                      <p className="text-sm text-arch-gray dark:text-gray-400 leading-relaxed">
                        Povrchy panelů jsou chráněny vrstvami odolnými proti skvrnám a poškrábání (např. PET scratch layer), 
                        které jsou k jádru pevně přichyceny pomocí PUR lepidel bez obsahu granulí. Tento proces zajišťuje, 
                        že se vrstvy neoddělují a panel zůstává kompaktní i při náročném používání.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Analogy Box */}
                <section className="bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border p-8 md:p-12 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-teal via-brand-500 to-accent-sage"></div>
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 bg-white dark:bg-dark-surface border border-arch-border dark:border-dark-border shrink-0 flex items-center justify-center rounded-full shadow-lg">
                      <SafeIcon icon={FiInfo} className="w-8 h-8 text-arch-dark dark:text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-arch-dark dark:text-white uppercase tracking-widest mb-4">Analogie pro pochopení</h3>
                      <p className="text-arch-gray dark:text-gray-300 leading-relaxed italic text-lg font-serif">
                        "Vlastnosti tohoto materiálu si můžete představit jako moderní sportovní vybavení, 
                        například tenisovou raketu z grafitových kompozitů. Je neuvěřitelně lehká v ruce, 
                        takže s ní můžete snadno manipulovat, ale zároveň je konstruována tak, aby vydržela obrovské nárazy a napětí, 
                        aniž by praskla nebo se ohnula. Stejně tak tyto panely nezatěžují vaši stavbu, ale ochrání ji s nekompromisní silou."
                      </p>
                    </div>
                  </div>
                </section>
              </motion.div>
            )}

            {activeTab !== 'lightweight' && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-32 flex flex-col items-center justify-center text-center border border-dashed border-arch-border dark:border-dark-border bg-white/50 dark:bg-dark-surface/50"
              >
                <div className="w-16 h-16 mb-6 text-arch-border dark:text-dark-border">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-arch-dark dark:text-white mb-2">Připravujeme</h3>
                <p className="text-arch-gray dark:text-gray-500 font-mono text-sm max-w-md">
                  Detailní dokumentace a parametry pro tuto vlastnost jsou momentálně ve fázi přípravy a budou brzy doplněny.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FeaturesExtended;